/**
 * Estados do card de comandos da tela simplificada de inversor.
 * A ordem de precedência ao resolver é:
 * comunicação → permissão → falha de carregamento → comandos cadastrados → habilitado.
 */
export type CommandsCardState =
    | 'NO_COMMUNICATION'
    | 'NO_PERMISSION'
    | 'LOAD_ERROR'
    | 'NO_COMMANDS'
    | 'ENABLED';

/**
 * Valores de `etsStatus` em que não há comunicação estabelecida com o equipamento.
 * `'Sincronizando'` entra aqui porque o vínculo ainda está sendo estabelecido — no Figma essa
 * tela repete o card de "sem comunicação", com o mesmo texto.
 */
const WITHOUT_COMMUNICATION_STATUSES = ['Não responde', 'Sincronizando'];

/**
 * `etsStatus` pode vir puro (`'Não responde'`) ou com a cor anexada (`'Não responde - f0a12b'`).
 * Aqui só interessa o nome do status.
 */
export const isWithoutCommunication = (status?: string): boolean => {
    if (!status) {
        return false;
    }
    return WITHOUT_COMMUNICATION_STATUSES.includes(status.split(' - ')[0].trim());
};

export interface ResolveCommandsCardStateParams {
    /** `etsStatus` do equipamento. */
    status?: string;
    /** Resultado da checagem de permissão de envio de comando. */
    isAuthorized: boolean;
    /** Quantidade de comandos cadastrados para o equipamento. */
    commandCount: number;
    /** `true` quando a busca de comandos falhou. */
    hasLoadError?: boolean;
}

export const resolveCommandsCardState = ({
    status,
    isAuthorized,
    commandCount,
    hasLoadError = false,
}: ResolveCommandsCardStateParams): CommandsCardState => {
    if (isWithoutCommunication(status)) {
        return 'NO_COMMUNICATION';
    }
    if (!isAuthorized) {
        return 'NO_PERMISSION';
    }
    if (hasLoadError) {
        return 'LOAD_ERROR';
    }
    if (commandCount === 0) {
        return 'NO_COMMANDS';
    }
    return 'ENABLED';
};

export interface CommandsCardContent {
    text: string;
    subText: string;
    buttonLabel: string;
}

export const COMMANDS_CARD_CONTENT: Record<CommandsCardState, CommandsCardContent> = {
    NO_COMMUNICATION: {
        buttonLabel: 'Comandos',
        subText: 'Verifique a comunicação do equipamento',
        text: 'Comandos indisponíveis',
    },
    NO_PERMISSION: {
        buttonLabel: 'Comandos',
        subText: 'Você não tem permissão para executar comandos.',
        text: 'Comandos indisponíveis',
    },
    LOAD_ERROR: {
        buttonLabel: 'Comandos',
        subText: 'Não foi possível carregar os comandos. Tente novamente.',
        text: 'Comandos indisponíveis',
    },
    NO_COMMANDS: {
        buttonLabel: 'Comandos',
        subText: 'Sem comandos cadastrados',
        text: 'Comandos indisponíveis',
    },
    ENABLED: {
        buttonLabel: 'Selecionar',
        subText: 'Selecione uma ação para o inversor',
        text: 'Comandos disponíveis',
    },
};
